// frontend/src/pages/Withdraw.jsx

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axiosAuth from "../axiosConfig";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const INR_STANDARD = { fee: 25, min: 1000, max: 500000, label: "Standard (3 business days)" };
const INR_INSTANT  = { fee: 125, min: 1000, max: 50000,  label: "Instant (2–3 hours)" };
const GST_RATE = 0.18;

const MIN_USD_HINT = { BTC: 12, ETH: 8, USDT: 5 };

const COINS = { BTC: "bitcoin", ETH: "ethereum", USDT: "tether" };
const NETWORKS = {
  BTC: ["BTC"],
  ETH: ["ERC20", "BEP20"],
  USDT: ["ERC20", "BEP20", "TRC20"],
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const toFixedSafe = (v, n = 6) => (Number.isFinite(v) ? Number(v).toFixed(n) : "0.000000");
const asNumber = (v) => {
  if (typeof v === "number") return v;
  if (typeof v !== "string") return 0;
  if (v.trim() === "" || v === ".") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function BadgeInfo() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 dark:bg-slate-800 dark:text-blue-300 px-2 py-1 text-[10px] md:text-[11px]">
      <span className="font-semibold">i</span>
      <span>Saved per address</span>
    </span>
  );
}

export default function WithdrawPage() {
  const navigate = useNavigate();

  // ── State
  const [balances, setBalances] = useState({});
  const [inrHistory, setInrHistory] = useState([]);
  const [cryptoHistory, setCryptoHistory] = useState([]);

  const [tab, setTab] = useState("INR");
  const [inrType, setInrType] = useState("Standard");
  const [inrAmount, setInrAmount] = useState("");

  const [coin, setCoin] = useState("BTC");
  const [network, setNetwork] = useState(NETWORKS["BTC"][0]);
  const [cryptoAmount, setCryptoAmount] = useState("");
  const [address, setAddress] = useState("");

  // OTP
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");

  // Live fee quote from backend
  const [feeQuote, setFeeQuote] = useState({ platform: 0, network: 0, total: 0 });
  const [quoting, setQuoting] = useState(false);
  const [quoteErr, setQuoteErr] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState("");

  // Beneficiary + Travel Rule (Crypto withdrawals)
  // Stored once per (user + network + address) by backend and reused for future withdrawals
  const [beneficiaryType, setBeneficiaryType] = useState("vasp"); // self_custody | vasp
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [vaspName, setVaspName] = useState("");
  const [vaspCountry, setVaspCountry] = useState("");
    const [relationship, setRelationship] = useState("");
  const [purpose, setPurpose] = useState("");

  // Optional: if you already checked in GTR portal manually, you can store the reference here
  
  // ───────────────────────────────────────────────────────────────────────────
  // Effects: histories/balances
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    axiosAuth.get("/withdraw/fiat")
      .then((res) => setInrHistory(res.data.withdrawals || []))
      .catch(() => {});

    axiosAuth.get("/withdraw/crypto")
      .then((res) => setCryptoHistory(res.data.withdrawals || []))
      .catch(() => {});

    (async () => {
      try {
        const { data: who } = await axiosAuth.get("/whoami");
        const { data: bal } = await axiosAuth.get(
          `/balance/${encodeURIComponent(who?.user?.email || "")}`
        );
        setBalances(bal?.balances ?? bal ?? {});
      } catch (err) {
        console.error("Balance fetch failed:", err);
      }
    })();


      // 🟦 Fetch bank accounts (left as-is; path unchanged)
    (async () => {
      try {
        const res = await axiosAuth.get("../bank/list");
        const list = res?.data?.banks || [];
        setBanks(list);
        const def = list.find((b) => b.isDefault);
        if (def) setSelectedBankId(def._id);
      } catch (err) {
        console.error("Bank list fetch failed:", err);
      }
    })();
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // Live fee quote for selected coin/network/amount
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const amt = asNumber(cryptoAmount);
    if (!coin || !network || amt <= 0) {
      setFeeQuote({ platform: 0, network: 0, total: 0 });
      setQuoteErr("");
      return;
    }
    let cancelled = false;
    setQuoting(true);
    setQuoteErr("");

    axiosAuth
      .get(`/withdraw/fees/network?coin=${coin}&network=${network}&amount=${amt}`)
      .then((res) => {
        if (cancelled) return;
        const pf = parseFloat(res?.data?.platformFeeCoin ?? 0) || 0;
        const nf = parseFloat(res?.data?.networkFeeCoin ?? res?.data?.feeInCoin ?? 0) || 0;
        const total = parseFloat(res?.data?.totalDeduct ?? (amt + pf + nf)) || (amt + pf + nf);
        setFeeQuote({ platform: pf, network: nf, total });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch fee quote:", err?.response?.data || err.message);
        setQuoteErr(err?.response?.data?.error || "Failed to fetch fees");
      })
      .finally(() => !cancelled && setQuoting(false));

    return () => { cancelled = true; };
  }, [coin, network, cryptoAmount]);

  // ───────────────────────────────────────────────────────────────────────────
  // Derived values
  // ───────────────────────────────────────────────────────────────────────────
  const inrConfig = inrType === "Standard" ? INR_STANDARD : INR_INSTANT;
  const inrAmtNum = asNumber(inrAmount);
  const inrValid = inrAmtNum >= inrConfig.min && inrAmtNum <= inrConfig.max;

  // INR: 0.5% with min ₹25/₹125 + 18% GST (unchanged)
  const pctFeeInr = +(inrAmtNum * 0.005).toFixed(2);
  const minFeeInr = inrType === "Instant" ? 125 : 25;
  const platformFeeInr = inrValid ? Math.max(pctFeeInr, minFeeInr) : 0;
  const gstInr = inrValid ? +(platformFeeInr * GST_RATE).toFixed(2) : 0;
  const inrNet = inrValid ? Math.max(0, +(inrAmtNum - platformFeeInr - gstInr).toFixed(2)) : 0;

  // Crypto (from backend quote)
  const cryptoAmtNum = asNumber(cryptoAmount);
  const platformFeeCoin = feeQuote.platform || 0;
  const networkFee = feeQuote.network || 0;
  const totalDeduction = cryptoAmtNum + platformFeeCoin + networkFee;

  const coinBal = Number(balances?.[coin] || 0);
  const maxWithdrawable = useMemo(() => {
    const m = coinBal - platformFeeCoin - networkFee;
    return m > 0 ? m : 0;
  }, [coinBal, platformFeeCoin, networkFee]);

  const showInrOtpBlock = inrValid;
  const showCryptoOtpBlock = cryptoAmtNum > 0 && !!address && Number.isFinite(networkFee);

  // Beneficiary validation (first-time address will require these on backend)
  const beneficiaryNameOk = tab !== "Crypto" ? true : beneficiaryName.trim().length >= 2;
  const beneficiaryTypeOk = tab !== "Crypto" ? true : ["self_custody", "vasp"].includes(String(beneficiaryType || ""));
  const vaspExtraOk =
    tab !== "Crypto" || beneficiaryType !== "vasp"
      ? true
      : vaspName.trim().length >= 2 && vaspCountry.trim().length >= 2;
  const beneficiaryValid = beneficiaryNameOk && beneficiaryTypeOk && vaspExtraOk;

  // ───────────────────────────────────────────────────────────────────────────
  // Crypto helpers (NEW): Max calculation + optional auto-prefill on coin/network change
  // ───────────────────────────────────────────────────────────────────────────
  const handleMaxClick = async () => {
    try {
      if (!coinBal || coinBal <= 0) return;
      setQuoting(true);
      const res = await axiosAuth.get(
        `/withdraw/fees/network?coin=${coin}&network=${network}&amount=${coinBal}`
      );
      const pf = parseFloat(res?.data?.platformFeeCoin ?? 0) || 0;
      const nf = parseFloat(res?.data?.networkFeeCoin ?? 0) || 0;
      const maxAmt = Math.max(coinBal - pf - nf, 0);
      setCryptoAmount(toFixedSafe(maxAmt));
      setFeeQuote({ platform: pf, network: nf, total: maxAmt + pf + nf });
    } catch (err) {
      console.error("Max fee quote error:", err);
      setCryptoAmount(toFixedSafe(coinBal));
    } finally {
      setQuoting(false);
    }
  };

  // Auto-fill max when coin/network changes and amount is empty (optional QoL)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (tab !== "Crypto") return;
      if (cryptoAmount) return; // don't override if user already typed
      if (!coinBal || coinBal <= 0) return;
      try {
        const res = await axiosAuth.get(
          `/withdraw/fees/network?coin=${coin}&network=${network}&amount=${coinBal}`
        );
        if (cancelled) return;
        const pf = parseFloat(res?.data?.platformFeeCoin ?? 0) || 0;
        const nf = parseFloat(res?.data?.networkFeeCoin ?? 0) || 0;
        const maxAmt = Math.max(coinBal - pf - nf, 0);
        setCryptoAmount(toFixedSafe(maxAmt));
        setFeeQuote({ platform: pf, network: nf, total: maxAmt + pf + nf });
      } catch (e) {
        // silent fail; user can still press Max
      }
    })();
    return () => { cancelled = true; };
  }, [coin, network, coinBal, tab]); // only crypto flow changed

  // ───────────────────────────────────────────────────────────────────────────
  // Actions
  // ───────────────────────────────────────────────────────────────────────────
  const refetchHistories = async () => {
    try {
      const [fiat, crypto] = await Promise.all([
        axiosAuth.get("/withdraw/fiat"),
        axiosAuth.get("/withdraw/crypto"),
      ]);
      setInrHistory(fiat?.data?.withdrawals || []);
      setCryptoHistory(crypto?.data?.withdrawals || []);
    } catch {}
  };

  const handleSendOtp = async () => {
    try {
      setSendingOtp(true);
      const res = await axiosAuth.post("/withdraw/request-otp");
      setOtpSent(true);
      setOtpMessage(res?.data?.message || "OTP sent to your email.");
      setOtp("");
    } catch (err) {
      setOtpMessage(err?.response?.data?.error || "Failed to send OTP.");
    } finally {
      setSendingOtp(false);
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      if (tab === "INR") {
        await axiosAuth.post("/withdraw/fiat", {
          amount: inrAmtNum,
          speed: inrType.toLowerCase(),
          bankId: selectedBankId,
          otp,
        });
        setInrAmount("");
        setOtpSent(false);
        setOtp("");
        await refetchHistories();
        alert("INR withdrawal submitted successfully! ✅");
      } else {
        await axiosAuth.post("/withdraw/crypto", {
          asset: coin,
          network,
          toAddress: address,
          amount: cryptoAmtNum,
          fee: networkFee,
          charge: platformFeeCoin,
          otp,

          beneficiary: {
            beneficiaryType,
            name: beneficiaryName,
            vaspName,
            vaspCountry,
            relationship,
            purpose,
          },
          travelRule: {
            provider: "gtr",
            status: "pending_manual",
          },
        });
        setCryptoAmount("");
        setAddress("");
        setOtpSent(false);
        setOtp("");
        await refetchHistories();
        alert("Crypto withdrawal submitted successfully! ✅");
      }
    } catch (err) {
      alert(err?.response?.data?.error || "Withdrawal failed");
    } finally {
      setSubmitting(false);
    }
  }

    // ───────────────────────────────────────────────────────────────────────────
  // Pagination hooks for Withdrawal History
  // ───────────────────────────────────────────────────────────────────────────
  const PAGE_SIZE = 10;
  const [historyPage, setHistoryPage] = useState(0);

  useEffect(() => {
    setHistoryPage(0);
  }, [tab, inrHistory.length, cryptoHistory.length]);

  const sortedHistory = useMemo(() => {
    const base = tab === "INR" ? inrHistory : cryptoHistory;
    return [...base].sort(
      (a, b) =>
        new Date(b?.date || b?.createdAt || 0) -
        new Date(a?.date || a?.createdAt || 0)
    );
  }, [tab, inrHistory, cryptoHistory]);

  const totalPages = Math.max(1, Math.ceil(sortedHistory.length / PAGE_SIZE));

  const pagedHistory = useMemo(() => {
    const start = historyPage * PAGE_SIZE;
    return sortedHistory.slice(start, start + PAGE_SIZE);
  }, [sortedHistory, historyPage]);


  // ───────────────────────────────────────────────────────────────────────────
  // UI — dark mode & compact mobile tweaks
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-slate-900"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Header */}
      <header className="bg-white dark:bg-slate-950 shadow-lg border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto w-full md:px-6 px-4 md:py-6 py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <div>
            <h1 className="text-base md:text-2xl font-semibold text-slate-900 dark:text-slate-100">
              Withdraw Funds
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-[11px] md:text-sm">
              Choose INR or Crypto withdrawal
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-2 sm:mt-0 text-blue-600 dark:text-blue-300 hover:underline text-xs md:text-sm"
          >
            ← Back
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto w-full md:px-6 px-3 md:mt-6 mt-3 md:space-y-8 space-y-3">
        {/* Quick Actions */}
        <motion.div
          className="bg-white dark:bg-slate-950 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-3 md:flex md:items-center md:justify-between md:gap-4 border-t-4 md:border-t-0 md:border-l-4 border-blue-500 sticky top-0 z-10 md:static backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-slate-950/80"
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-blue-700 dark:text-blue-300 font-medium md:mb-0 mb-2 text-sm md:text-base">
            Quick Actions
          </p>
          <div className="flex flex-wrap gap-2 md:gap-3 md:ml-auto">
            <button
              onClick={() => navigate("/deposit")}
              className="bg-blue-600 text-white px-3 md:px-5 py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base active:scale-[0.99]"
            >
              Deposit
            </button>
            <button
              onClick={() => navigate("/quick-trade")}
              className="bg-blue-600 text-white px-3 md:px-5 py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base active:scale-[0.99]"
            >
              Quick Trade
            </button>
            <button
              onClick={() => navigate("/exchange")}
              className="bg-blue-600 text-white px-3 md:px-5 py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base active:scale-[0.99]"
            >
              Exchange
            </button>
            <button
              onClick={() => navigate("/p2p")}
              className="bg-blue-600 text-white px-3 md:px-5 py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base active:scale-[0.99]"
            >
              P2P Market
            </button>
          </div>
        </motion.div>

        {/* Form Card */}
        <motion.div
          className="bg-white dark:bg-slate-950 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-3 border-t-4 border-blue-500"
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          {/* Tabs */}
          <nav className="flex justify-center gap-4 md:gap-6 border-b border-slate-200 dark:border-slate-800 pb-1.5 md:pb-2 md:mb-4 mb-3">
            {["INR", "Crypto"].map((t) => (
              <button
                key={t}
                onClick={() => { setTab(t); setOtpSent(false); setOtp(""); }}
                className={`px-2.5 md:px-3 py-1.5 text-xs md:text-sm font-medium rounded-t-md transition-colors ${
                  tab === t
                    ? "text-blue-700 dark:text-blue-300 border-b-2 border-blue-600"
                    : "text-slate-600 dark:text-slate-300 hover:text-blue-700 dark:hover:text-blue-300"
                }`}
              >
                {t}
              </button>
            ))}
          </nav>

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
            {tab === "INR" ? (
              <>
                {/* 🟦 Bank selection dropdown (unchanged) */}
                <div>
                  <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                    Select Bank Account
                  </label>
                  <select
                    value={selectedBankId}
                    onChange={(e) => setSelectedBankId(e.target.value)}
                    className="w-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select Bank --</option>
                    {banks.length > 0 ? (
                      banks.map((b) => (
                        <option key={b._id} value={b._id}>
                          {b.bankName} ({b.accountNumber}) — {b.status === "verified" ? "✅ Verified" : "⏳ Pending"}
                          {b.isDefault ? " ⭐" : ""}
                        </option>
                      ))
                    ) : (
                      <option disabled> No bank accounts found </option>
                    )}
                  </select>
                  {banks.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      No bank added yet. Please add one from your Profile page.
                    </p>
                  )}
                </div>

                {/* Options (unchanged) */}
                <div className="flex flex-wrap gap-3 md:gap-4">
                  {["Standard", "Instant"].map((type) => (
                    <label key={type} className="inline-flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-slate-800 dark:text-slate-200">
                      <input
                        type="radio"
                        name="inrType"
                        value={type}
                        checked={inrType === type}
                        onChange={() => setInrType(type)}
                        className="text-blue-600"
                      />
                      <span>{type === "Standard" ? INR_STANDARD.label : INR_INSTANT.label}</span>
                    </label>
                  ))}
                </div>

                {/* Amount (unchanged) */}
                <div>
                  <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                    Amount (₹)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="^[0-9]*$"
                    value={inrAmount}
                    onChange={(e) => setInrAmount(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder={`₹${inrConfig.min} - ₹${inrConfig.max}`}
                    className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  <div className="flex justify-between text-[10px] md:text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    <span>Available: ₹{Number(balances?.INR || 0).toLocaleString()}</span>
                    {!inrValid && inrAmount && (
                      <span className="text-red-600 dark:text-red-400">
                        Must be between ₹{inrConfig.min} and ₹{inrConfig.max}
                      </span>
                    )}
                  </div>
                </div>

                {/* Summary + OTP (unchanged) */}
                {inrValid && (
                  <div className="grid grid-cols-1 gap-2.5 md:gap-3">
                    <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-2.5 md:p-3 text-xs md:text-sm text-slate-800 dark:text-slate-200">
                      <div className="flex justify-between py-0.5"><span>Amount</span><span>₹{inrAmtNum.toLocaleString()}</span></div>
                      <div className="flex justify-between py-0.5"><span>Platform fee</span><span>₹{platformFeeInr.toLocaleString()}</span></div>
                      <div className="flex justify-between py-0.5"><span>GST (18%)</span><span>₹{gstInr.toLocaleString()}</span></div>
                      <div className="flex justify-between py-0.5 font-semibold text-slate-900 dark:text-slate-100"><span>Net</span><span>₹{inrNet.toLocaleString()}</span></div>
                    </div>

                    {!otpSent ? (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        className="w-full text-xs md:text-sm bg-blue-600 text-white px-3 py-2 rounded-md font-medium hover:bg-blue-700 disabled:opacity-60 active:scale-[0.99]"
                        disabled={sendingOtp}
                      >
                        {sendingOtp ? "Sending OTP…" : "Send OTP to Email"}
                      </button>
                    ) : (
                      <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-2.5 md:p-3 space-y-2">
                        <label className="block text-slate-700 dark:text-slate-200 font-medium text-xs md:text-sm">
                          Enter OTP
                        </label>
                        <input
                              type="tel"
                              inputMode="numeric"
                              maxLength={6}
                              value={otp}
                              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                              autoComplete="one-time-code"
                              className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                              placeholder="6-digit OTP"
                            />
                        {otpMessage && (
                          <div className="text-[10px] md:text-[11px] bg-blue-50 dark:bg-slate-900 border-l-4 border-blue-600 text-blue-800 dark:text-blue-300 px-3 py-2 rounded">
                            {otpMessage}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          className="w-full text-[11px] md:text-xs text-blue-700 dark:text-blue-300 underline"
                          disabled={sendingOtp}
                        >
                          Resend OTP
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Coin & Network (crypto) */}
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                      Coin
                    </label>
                    <select
                      value={coin}
                      onChange={(e) => {
                        const c = e.target.value;
                        setCoin(c);
                        setNetwork(NETWORKS[c][0]);
                        setOtpSent(false);
                        setOtp("");
                        // amount auto-prefill handled by effect
                      }}
                      className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      {Object.keys(COINS).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                      Network
                    </label>
                    <select
                      value={network}
                      onChange={(e) => setNetwork(e.target.value)}
                      className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      {NETWORKS[coin].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Crypto Amount */}
                <div>
                  <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                    Amount ({coin})
                  </label>
                  <div className="flex items-stretch">
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern=".*"
                      value={cryptoAmount}
                      onChange={(e) => setCryptoAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                      placeholder={`Max ${toFixedSafe(maxWithdrawable)} ${coin}`}
                      onWheel={(e) => e.currentTarget.blur()}
                      className="flex-1 text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-l-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleMaxClick}
                      className="px-3 md:px-4 text-xs md:text-sm font-semibold bg-blue-600 text-white rounded-r-md hover:bg-blue-700 active:scale-[0.99] disabled:opacity-60"
                      disabled={quoting || !coinBal || coinBal <= 0}
                      title="Set max withdrawable amount"
                    >
                      {quoting ? "…" : "Max"}
                    </button>
                  </div>

                  <div className="flex justify-between text-[10px] md:text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                    <span>Available: {toFixedSafe(coinBal)} {coin}</span>
                    {totalDeduction - coinBal > 1e-6 && cryptoAmount && (
                      <span className="text-red-600 dark:text-red-400">
                        Insufficient balance (need {toFixedSafe(totalDeduction)} {coin})
                      </span>
                    )}
                  </div>
                </div>

                {/* Receiver Address */}
                <div>
                  <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                    Receiver Address
                  </label>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value.trim())}
                    placeholder="0x… / bc1… / T…"
                    className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>

                {/* Beneficiary details + Travel Rule (required once per address; reused later) */}
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-2.5 md:p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-slate-900 dark:text-slate-100 font-semibold text-xs md:text-sm">
                        Beneficiary Details
                      </p>
                      <p className="text-[10px] md:text-[11px] text-slate-600 dark:text-slate-400">
                        This is required the first time you withdraw to an address. We save it and reuse it for future withdrawals to the same address.
                      </p>
                    </div>
                    <BadgeInfo />
                  </div>

                  <div className="grid md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                        Beneficiary Type
                      </label>
                      <select
                        value={beneficiaryType}
                        onChange={(e) => setBeneficiaryType(e.target.value)}
                        className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      >
                        <option value="vasp"> Other Exchange wallet</option>
                        <option value="self_custody">Self-custody (My wallet)</option> 
                      </select>
                    </div>

                    <div>
                      <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                        Beneficiary Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={beneficiaryName}
                        onChange={(e) => setBeneficiaryName(e.target.value)}
                        placeholder="Name of the address owner"
                        className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                      {!beneficiaryNameOk && (
                        <p className="text-[10px] md:text-[11px] text-red-600 dark:text-red-400 mt-1">
                          Beneficiary name is required.
                        </p>
                      )}
                    </div>
                  </div>

                  {beneficiaryType === "vasp" && (
                    <div className="grid md:grid-cols-3 gap-2">
                      <div className="md:col-span-1">
                        <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                          Platform Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={vaspName}
                          onChange={(e) => setVaspName(e.target.value)}
                          placeholder="e.g., Binance"
                          className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>
                      <div className="md:col-span-1">
                        <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                          Country <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={vaspCountry}
                          onChange={(e) => setVaspCountry(e.target.value)}
                          placeholder="e.g., UAE / India"
                          className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                      </div>

                      {!vaspExtraOk && (
                        <div className="md:col-span-3">
                          <p className="text-[10px] md:text-[11px] text-red-600 dark:text-red-400">
                            Name and country are required.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-2">
                    <div>
                      <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                        Relationship (optional)
                      </label>
                      <input
                        type="text"
                        value={relationship}
                        onChange={(e) => setRelationship(e.target.value)}
                        placeholder="e.g., Self / Family / Business"
                        className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-slate-700 dark:text-slate-200 font-medium mb-1 text-xs md:text-sm">
                        Purpose (optional)
                      </label>
                      <input
                        type="text"
                        value={purpose}
                        onChange={(e) => setPurpose(e.target.value)}
                        placeholder="e.g., Personal transfer"
                        className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>
                  </div>

                  <div className="text-[10px] md:text-[11px] text-slate-600 dark:text-slate-400">
                    Withdrawals are processed after compliance screening (KYT) and verification by our team.
                  </div>
                </div>

                {/* OTP block (crypto) */}
                {showCryptoOtpBlock && (
                  <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-2.5 md:p-3">
                    {!otpSent ? (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        className="w-full text-xs md:text-sm bg-blue-600 text-white px-3 py-2 rounded-md font-medium hover:bg-blue-700 disabled:opacity-60 active:scale-[0.99]"
                        disabled={sendingOtp}
                      >
                        {sendingOtp ? "Sending OTP…" : "Send OTP to Email"}
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <label className="block text-slate-700 dark:text-slate-200 font-medium text-xs md:text-sm">
                          Enter OTP
                        </label>
                        <input
                          type="tel"
                          inputMode="numeric"
                          maxLength={6}
                          value={otp}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
                            setOtp(val);
                          }}
                          autoComplete="one-time-code"
                          className="w-full text-sm md:text-base border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                          placeholder="6-digit OTP"
                        />
                        {otpMessage && (
                          <div className="text-[10px] md:text-[11px] bg-blue-50 dark:bg-slate-900 border-l-4 border-blue-600 text-blue-800 dark:text-blue-300 px-3 py-2 rounded">
                            {otpMessage}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          className="w-full text-[11px] md:text-xs text-blue-700 dark:text-blue-300 underline"
                          disabled={sendingOtp}
                        >
                          Resend OTP
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Summary */}
                {cryptoAmount && address && (
                  <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-2.5 md:p-3 text-xs md:text-sm text-slate-800 dark:text-slate-200 space-y-1">
                    <div className="flex justify-between"><span>You are sending</span><span>{toFixedSafe(cryptoAmtNum)} {coin}</span></div>
                    <div className="flex justify-between"><span>Network fee</span><span>{quoting ? "…" : toFixedSafe(networkFee)} {coin}</span></div>
                    <div className="flex justify-between">
                      <span>Platform fee</span>
                      <span>
                        {quoting ? "…" : toFixedSafe(platformFeeCoin)} {coin}
                        <span className="text-slate-500 dark:text-slate-400 text-[10px] md:text-[11px]">&nbsp;(min ${MIN_USD_HINT[coin] || 5})</span>
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-slate-900 dark:text-slate-100">
                      <span>Total deduction</span>
                      <span>{quoting ? "…" : toFixedSafe(totalDeduction)} {coin}</span>
                    </div>
                    {quoteErr && (
                      <div className="text-[10px] md:text-[11px] text-amber-700 dark:text-amber-400">
                        {quoteErr}. Fees will be finalized on submit.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Submit */}
              <button
                type="submit"
                className={`w-full text-sm md:text-base px-4 py-2.5 rounded-md font-semibold active:scale-[0.99] transition-all ${
                  submitting ||
                  (tab === "INR"
                    ? !inrValid || otp.length !== 6
                    : totalDeduction - coinBal > 1e-6 || otp.length !== 6 || !beneficiaryValid)
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
                disabled={
                  submitting ||
                  (tab === "INR"
                    ? !inrValid || otp.length !== 6
                    : totalDeduction - coinBal > 1e-6 || otp.length !== 6 || !beneficiaryValid)
                }
              >
                {submitting ? "Submitting…" : "Withdraw"}
              </button>
          </form>
        </motion.div>
        {/* History */}
        <motion.div
          className="bg-white dark:bg-slate-950 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-3 border-t-4 border-blue-500"
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <h2 className="md:text-xl text-base font-semibold text-slate-900 dark:text-slate-100 md:mb-4 mb-2">
            Recent {tab} Withdrawals
          </h2>

          {/* ─────────────────────────────────────────────────────────────── */}
          {/* Pagination & sorted withdrawal history (hooks moved to top-level) */}
          {/* ─────────────────────────────────────────────────────────────── */}

          <>
            {/* Mobile cards */}
            <div className="md:hidden space-y-2">
              {sortedHistory.length === 0 ? (
                <div className="text-center text-slate-500 dark:text-slate-400 text-xs py-4">
                  No withdrawals yet.
                </div>
              ) : (
                pagedHistory.map((w) => (
                  <div
                    key={w.id || w._id}
                    className="rounded-lg border border-blue-100 dark:border-slate-800 bg-blue-50/40 dark:bg-slate-900 p-3"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-xs font-medium text-blue-800 dark:text-blue-300">
                        {tab === "INR" ? "INR" : w.coin || "Crypto"}
                      </div>
                      <span className="text-[10px] text-slate-600 dark:text-slate-400">
                        {new Date(w.date || w.createdAt).toLocaleString()}
                      </span>
                    </div>

                    {tab === "INR" ? (
                      <div className="text-[11px] text-slate-800 dark:text-slate-200 space-y-0.5">
                        <div className="flex justify-between">
                          <span>Amount</span>
                          <span>₹{Number(w.amount || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Platform Fee</span>
                          <span>
                            ₹{Number(w.platformFee ?? w.charge ?? 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>GST</span>
                          <span>₹{Number(w.gst || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between font-semibold">
                          <span>Net</span>
                          <span>
                            ₹
                            {Number(
                              w.net ??
                                w.netAmount ??
                                (Number(w.amount || 0) -
                                  Number(w.platformFee ?? w.charge ?? 0) -
                                  Number(w.gst || 0))
                            ).toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-1 text-[10px] text-slate-600 dark:text-slate-400 truncate">
                          Txn: {w.transactionId || w.txnId || "—"}
                        </div>
                        <div className="mt-1">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-700 dark:bg-slate-800 dark:text-blue-300">
                            {w.status}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-800 dark:text-slate-200 space-y-0.5">
                        <div className="flex justify-between">
                          <span>Coin</span>
                          <span>{w.coin}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Amount</span>
                          <span>{w.amount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Platform Fee</span>
                          <span>{w.platformFee}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Network Fee</span>
                          <span>{w.networkFee}</span>
                        </div>
                        <div className="mt-1 text-[10px] text-slate-600 dark:text-slate-400 truncate">
                          To: {w.toAddress || w.address || "—"}
                        </div>
                        <div className="mt-1 text-[10px] text-slate-600 dark:text-slate-400 truncate">
                          Txn: {w.txid || w.transactionId || "—"}
                        </div>
                        <div className="mt-1">
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-700 dark:bg-slate-800 dark:text-blue-300">
                            {w.status}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              {sortedHistory.length === 0 ? (
                <div className="text-center text-slate-500 dark:text-slate-400 text-sm py-6">
                  No withdrawals yet.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-slate-700 dark:text-slate-200">
                    <tr className="border-b border-slate-200 dark:border-slate-800">
                      <th className="text-left py-2">Date</th>
                      {tab === "INR" ? (
                        <>
                          <th className="text-left py-2">Amount</th>
                          <th className="text-left py-2">Fee</th>
                          <th className="text-left py-2">GST</th>
                          <th className="text-left py-2">Net</th>
                          <th className="text-left py-2">Txn</th>
                          <th className="text-left py-2">Status</th>
                        </>
                      ) : (
                        <>
                          <th className="text-left py-2">Coin</th>
                          <th className="text-left py-2">Amount</th>
                          <th className="text-left py-2">Platform Fee</th>
                          <th className="text-left py-2">Network Fee</th>
                          <th className="text-left py-2">To</th>
                          <th className="text-left py-2">Txn</th>
                          <th className="text-left py-2">Status</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="text-slate-800 dark:text-slate-200">
                    {pagedHistory.map((w) => (
                      <tr
                        key={w.id || w._id}
                        className="border-b border-slate-100 dark:border-slate-800"
                      >
                        <td className="py-2">
                          {new Date(w.date || w.createdAt).toLocaleString()}
                        </td>

                        {tab === "INR" ? (
                          <>
                            <td className="py-2">₹{Number(w.amount || 0).toLocaleString()}</td>
                            <td className="py-2">
                              ₹{Number(w.platformFee ?? w.charge ?? 0).toLocaleString()}
                            </td>
                            <td className="py-2">₹{Number(w.gst || 0).toLocaleString()}</td>
                            <td className="py-2 font-semibold">
                              ₹
                              {Number(
                                w.net ??
                                  w.netAmount ??
                                  (Number(w.amount || 0) -
                                    Number(w.platformFee ?? w.charge ?? 0) -
                                    Number(w.gst || 0))
                              ).toLocaleString()}
                            </td>
                            <td className="py-2">
                              {w.transactionId || w.txnId || "—"}
                            </td>
                            <td className="py-2">
                              <span className="inline-block px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-slate-800 dark:text-blue-300">
                                {w.status}
                              </span>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="py-2">{w.coin}</td>
                            <td className="py-2">{w.amount}</td>
                            <td className="py-2">{w.platformFee}</td>
                            <td className="py-2">{w.networkFee}</td>
                            <td className="py-2 truncate max-w-[220px]">
                              {w.toAddress || w.address || "—"}
                            </td>
                            <td className="py-2">{w.txid || w.transactionId || "—"}</td>
                            <td className="py-2">
                              <span className="inline-block px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700 dark:bg-slate-800 dark:text-blue-300">
                                {w.status}
                              </span>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination controls */}
            {sortedHistory.length > 0 && (
              <div className="flex items-center justify-between mt-3">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs md:text-sm disabled:opacity-50"
                  onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                  disabled={historyPage === 0}
                >
                  ← Prev
                </button>

                <div className="text-[10px] md:text-xs text-slate-600 dark:text-slate-400">
                  Page {historyPage + 1} / {totalPages}
                </div>

                <button
                  type="button"
                  className="px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-200 text-xs md:text-sm disabled:opacity-50"
                  onClick={() => setHistoryPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={historyPage >= totalPages - 1}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        </motion.div>
      </main>
    </motion.div>
  );
}
